import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import api from '../lib/api';
import {
  ArrowLeft,
  CheckCircle,
  Circle,
  Camera,
  AlertCircle,
  Send,
  XCircle,
  ChevronRight,
  ClipboardList,
} from 'lucide-react';

interface ChecklistTemplate {
  id: string;
  name: string;
  description: string | null;
  channel_id: string | null;
  channel_name: string | null;
  is_active: boolean;
  created_at: string;
  items_count: number;
}

interface ChecklistItem {
  id: string;
  question: string;
  type: 'BOOLEAN' | 'TEXT' | 'NUMBER' | 'PHOTO' | 'MULTIPLE_CHOICE' | 'SCALE';
  required: boolean;
  options: string[] | null;
  order: number;
  weight: number;
}

interface ChecklistTemplateDetail extends ChecklistTemplate {
  items: ChecklistItem[];
}

export default function Checklist({ visitId }: { visitId: string }) {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Map<string, string | number | boolean>>(new Map());
  const [photoUrls, setPhotoUrls] = useState<Map<string, string>>(new Map());

  // Fetch templates list
  const { data: templates, isLoading: isLoadingTemplates } = useQuery<ChecklistTemplate[]>({
    queryKey: ['checklist-templates'],
    queryFn: () =>
      api.get('/checklists/templates?is_active=true').then((res) => res.data.data),
  });

  // Fetch template detail when one is selected
  const { data: templateDetail, isLoading: isLoadingDetail } = useQuery<ChecklistTemplateDetail>({
    queryKey: ['checklist-template', selectedTemplateId],
    queryFn: () =>
      api
        .get(`/checklists/templates/${selectedTemplateId}`)
        .then((res) => res.data.data),
    enabled: !!selectedTemplateId,
  });

  // Submit checklist mutation
  const submitMutation = useMutation({
    mutationFn: async () => {
      const responses = Array.from(answers.entries()).map(([item_id, value]) => ({
        item_id,
        value,
        photo_url: photoUrls.get(item_id),
      }));

      const response = await api.post('/checklists/submit', {
        visit_id: visitId,
        template_id: selectedTemplateId,
        responses,
      });

      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visit', 'active', visitId] });
      queryClient.invalidateQueries({ queryKey: ['checklist', visitId] });
      setLocation(`/visit/${visitId}`);
    },
    onError: (error: any) => {
      alert(error.response?.data?.error?.message || 'Erro ao enviar checklist');
    },
  });

  const handleAnswerChange = (itemId: string, value: string | number | boolean) => {
    const newAnswers = new Map(answers);
    newAnswers.set(itemId, value);
    setAnswers(newAnswers);
  };

  const [uploadingPhoto, setUploadingPhoto] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingItemIdRef = useRef<string | null>(null);

  const handlePhotoCapture = (itemId: string) => {
    pendingItemIdRef.current = itemId;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const itemId = pendingItemIdRef.current;
    if (!file || !itemId) return;
    e.target.value = ''; // reset input

    setUploadingPhoto(itemId);
    try {
      const formData = new FormData();
      formData.append('photo', file, `checklist_${Date.now()}.jpg`);
      formData.append('photo_type', 'OTHER');
      const res = await api.post('/photos/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data.data.url;
      setPhotoUrls(prev => {
        const next = new Map(prev);
        next.set(itemId, url);
        return next;
      });
    } catch {
      alert('Erro ao enviar foto. Tente novamente.');
    } finally {
      setUploadingPhoto(null);
    }
  };

  const handleSubmit = () => {
    if (!templateDetail) return;

    const requiredUnanswered = templateDetail.items.filter(
      (item) => item.required && !answers.has(item.id)
    );
    if (requiredUnanswered.length > 0) {
      if (
        !confirm(
          `Você tem ${requiredUnanswered.length} item(ns) obrigatório(s) não respondido(s). Deseja enviar mesmo assim?`
        )
      ) {
        return;
      }
    }

    submitMutation.mutate();
  };

  const handleBack = () => {
    if (selectedTemplateId) {
      setSelectedTemplateId(null);
      setAnswers(new Map());
      setPhotoUrls(new Map());
    } else {
      setLocation(`/visit/${visitId}`);
    }
  };

  // --- Loading state ---
  if (isLoadingTemplates || (selectedTemplateId && isLoadingDetail)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  // --- Template selection screen ---
  if (!selectedTemplateId || !templateDetail) {
    const hasTemplates = templates && templates.length > 0;

    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <header className="bg-primary text-white p-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setLocation(`/visit/${visitId}`)}
              className="p-2 -ml-2 rounded-full hover:bg-white/20"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <h1 className="text-lg font-bold">Selecionar Checklist</h1>
          </div>
        </header>

        <main className="flex-1 p-4">
          {!hasTemplates ? (
            <div className="flex flex-col items-center justify-center h-full py-16">
              <AlertCircle className="h-16 w-16 text-gray-400 mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Nenhum checklist disponível
              </h2>
              <p className="text-gray-600 mb-6 text-center">
                Não há checklists ativos para esta visita
              </p>
              <button
                onClick={() => setLocation(`/visit/${visitId}`)}
                className="px-6 py-3 bg-primary text-white rounded-lg font-semibold"
              >
                Voltar para visita
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-4">
                Selecione o checklist que deseja preencher:
              </p>
              {templates!.map((template) => (
                <button
                  key={template.id}
                  onClick={() => setSelectedTemplateId(template.id)}
                  className="w-full bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-4 text-left hover:border-primary/30 transition-colors"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <ClipboardList className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900">{template.name}</p>
                    {template.description && (
                      <p className="text-sm text-gray-500 truncate">{template.description}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      {Number(template.items_count)} {Number(template.items_count) === 1 ? 'item' : 'itens'}
                      {template.channel_name ? ` · ${template.channel_name}` : ''}
                    </p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </main>
      </div>
    );
  }

  // --- Checklist form screen ---
  const items = templateDetail.items.sort((a, b) => a.order - b.order);
  const totalItems = items.length;
  const answeredItems = answers.size;
  const completionPercentage =
    totalItems > 0 ? Math.round((answeredItems / totalItems) * 100) : 0;

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-primary text-white p-4 pb-8">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={handleBack}
            className="p-2 -ml-2 rounded-full hover:bg-white/20"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">{templateDetail.name}</h1>
          </div>
        </div>

        {/* Progress Card */}
        <div className="bg-white rounded-xl p-4 text-gray-900 shadow-lg -mb-12">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-gray-600">Progresso</span>
            <span className="text-lg font-bold text-primary">
              {answeredItems}/{totalItems}
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">{completionPercentage}% completo</p>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 p-4 pt-16 pb-24 space-y-4">
        {items.map((item) => {
          const answer = answers.get(item.id);
          const hasPhoto = photoUrls.has(item.id);

          return (
            <div
              key={item.id}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-4"
            >
              <div className="flex items-start gap-2 mb-3">
                <p className="text-gray-900 flex-1">{item.question}</p>
                {item.required && (
                  <span className="text-xs text-red-500 font-medium mt-0.5 flex-shrink-0">*</span>
                )}
              </div>

              {/* BOOLEAN */}
              {item.type === 'BOOLEAN' && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleAnswerChange(item.id, true)}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-medium transition-colors ${
                      answer === true
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <CheckCircle className="h-5 w-5" />
                    Sim
                  </button>
                  <button
                    onClick={() => handleAnswerChange(item.id, false)}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-medium transition-colors ${
                      answer === false
                        ? 'bg-red-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <XCircle className="h-5 w-5" />
                    Não
                  </button>
                </div>
              )}

              {/* TEXT */}
              {item.type === 'TEXT' && (
                <textarea
                  className="w-full border border-gray-200 rounded-lg p-3 text-gray-900 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                  rows={3}
                  placeholder="Digite sua resposta..."
                  value={typeof answer === 'string' ? answer : ''}
                  onChange={(e) => handleAnswerChange(item.id, e.target.value)}
                />
              )}

              {/* NUMBER */}
              {item.type === 'NUMBER' && (
                <input
                  type="number"
                  className="w-full border border-gray-200 rounded-lg p-3 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="Digite um número..."
                  value={typeof answer === 'number' ? answer : ''}
                  onChange={(e) =>
                    handleAnswerChange(item.id, e.target.value === '' ? '' : Number(e.target.value))
                  }
                />
              )}

              {/* SCALE */}
              {item.type === 'SCALE' && (
                <div className="space-y-2">
                  <input
                    type="range"
                    min={1}
                    max={10}
                    step={1}
                    className="w-full accent-primary"
                    value={typeof answer === 'number' ? answer : 5}
                    onChange={(e) => handleAnswerChange(item.id, Number(e.target.value))}
                    onMouseDown={() => {
                      if (!answers.has(item.id)) handleAnswerChange(item.id, 5);
                    }}
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>1</span>
                    <span className="font-semibold text-primary">
                      {typeof answer === 'number' ? answer : '—'}
                    </span>
                    <span>10</span>
                  </div>
                </div>
              )}

              {/* MULTIPLE_CHOICE */}
              {item.type === 'MULTIPLE_CHOICE' && item.options && item.options.length > 0 && (
                <div className="space-y-2">
                  {item.options.map((option) => (
                    <button
                      key={option}
                      onClick={() => handleAnswerChange(item.id, option)}
                      className={`w-full flex items-center gap-3 py-2.5 px-3 rounded-lg font-medium text-sm transition-colors text-left ${
                        answer === option
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {answer === option ? (
                        <CheckCircle className="h-4 w-4 flex-shrink-0" />
                      ) : (
                        <Circle className="h-4 w-4 flex-shrink-0" />
                      )}
                      {option}
                    </button>
                  ))}
                </div>
              )}

              {/* PHOTO */}
              {item.type === 'PHOTO' && (
                <div>
                  {uploadingPhoto === item.id ? (
                    <div className="flex items-center justify-center gap-2 py-2 text-sm text-blue-600">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                      <span>Enviando foto...</span>
                    </div>
                  ) : hasPhoto ? (
                    <div className="flex items-center gap-2 text-sm text-green-600">
                      <CheckCircle className="h-4 w-4" />
                      <span>Foto anexada</span>
                      <button
                        onClick={() => handlePhotoCapture(item.id)}
                        className="ml-auto text-primary hover:underline"
                      >
                        Alterar
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        handleAnswerChange(item.id, 'photo');
                        handlePhotoCapture(item.id);
                      }}
                      className="w-full flex items-center justify-center gap-2 py-2 bg-blue-50 text-blue-600 rounded-lg font-medium hover:bg-blue-100"
                    >
                      <Camera className="h-4 w-4" />
                      Tirar foto
                    </button>
                  )}
                </div>
              )}

              {/* Photo evidence for non-PHOTO types */}
              {item.type === 'BOOLEAN' && answer === true && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  {hasPhoto ? (
                    <div className="flex items-center gap-2 text-sm text-green-600">
                      <CheckCircle className="h-4 w-4" />
                      <span>Foto anexada</span>
                      <button
                        onClick={() => handlePhotoCapture(item.id)}
                        className="ml-auto text-primary hover:underline"
                      >
                        Alterar
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handlePhotoCapture(item.id)}
                      className="w-full flex items-center justify-center gap-2 py-2 bg-blue-50 text-blue-600 rounded-lg font-medium hover:bg-blue-100"
                    >
                      <Camera className="h-4 w-4" />
                      Adicionar foto (opcional)
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </main>

      {/* Fixed Submit Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
        <button
          onClick={handleSubmit}
          disabled={submitMutation.isPending || answeredItems === 0}
          className="w-full flex items-center justify-center gap-2 py-4 bg-primary text-white rounded-xl font-semibold active:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitMutation.isPending ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Enviando...</span>
            </>
          ) : (
            <>
              <Send className="h-5 w-5" />
              <span>Enviar Checklist</span>
            </>
          )}
        </button>

        {answeredItems > 0 && answeredItems < totalItems && (
          <p className="text-xs text-gray-500 text-center mt-2">
            {totalItems - answeredItems} item(ns) pendente(s)
          </p>
        )}
      </div>

      {/* Hidden file input for photo capture */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelected}
        className="hidden"
      />
    </div>
  );
}
